import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../features/userSlice";
import { dealsPromotionsEndpointLogic } from "../../../logic/Operations/DealsPromotionsEndpointLogic";
import store from "../../../store";
import {
  updateDealsPortfolioPlan,
  setLastUpdatedPlanId,
  setDealsSuccessMessage,
} from "../../../features/dealsPromotionsSlice";

interface Props {
  row: any;
  currencyOptions: { label: string; uid: string; symbol: string }[];
  disabled?: boolean;
}

export default function DealsAndPromotionsEditModal({
  row,
  currencyOptions,
  disabled = false,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const [cpa, setCpa] = React.useState<string>("");
  const [rsPercent, setRsPercent] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = React.useState<any>(null);
  const [touched, setTouched] = React.useState({
    cpa: false,
    rsPercent: false,
    currency: false,
  });

  const cpaError = touched.cpa
    ? cpa === ""
      ? "CPA is required"
      : !Number.isInteger(Number(cpa)) || Number(cpa) < 0 || cpa.includes(".")
        ? "Must be a non-negative integer"
        : cpa.length > 11
          ? "Max 11 digits"
          : null
    : null;

  const rsError = touched.rsPercent
    ? rsPercent === ""
      ? "RS % is required"
      : !Number.isInteger(Number(rsPercent)) ||
          Number(rsPercent) < 0 ||
          rsPercent.includes(".")
        ? "Must be a non-negative integer"
        : Number(rsPercent) > 100
          ? "Must be 100 or less"
          : null
    : null;

  const currencyError =
    touched.currency && !selectedCurrency ? "Currency is required" : null;

  const descError = description.length > 255 ? "Max 255 characters" : null;

  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const currencies = useSelector(
    (state: any) => state.dealsPromotions.currencies,
  );

  const openDrawer = () => {
    setMounted(true);
    setApiError(null);
    setTouched({ cpa: false, rsPercent: false, currency: false });
    setCpa(row.cpa != null ? String(row.cpa) : "");
    setRsPercent(row.rs_percent != null ? String(row.rs_percent) : "");
    setDescription(row.description ?? "");
    const matched = (currencies ?? []).find(
      (c: any) => c.shared_uid === row.currency_shared_uid,
    );
    setSelectedCurrency(
      matched
        ? {
            label: matched.currency_code,
            uid: matched.shared_uid,
            symbol: matched.currency_symbol ?? "",
          }
        : null,
    );
    setOpen(true);
  };

  const closeDrawer = () => {
    setOpen(false);
    setTimeout(() => setMounted(false), 300);
  };

  const handleSave = async () => {
    setTouched({ cpa: true, rsPercent: true, currency: true });
    const cpaBad =
      cpa === "" ||
      !Number.isInteger(Number(cpa)) ||
      Number(cpa) < 0 ||
      cpa.includes(".") ||
      cpa.length > 11;
    const rsBad =
      rsPercent === "" ||
      !Number.isInteger(Number(rsPercent)) ||
      Number(rsPercent) < 0 ||
      Number(rsPercent) > 100 ||
      rsPercent.includes(".");
    const currBad = !selectedCurrency;
    if (cpaBad || rsBad || currBad || !!descError) return;
    setIsSaving(true);
    const body = {
      currency_shared_uid: selectedCurrency?.uid ?? null,
      cpa: cpa !== "" ? Number(cpa) : null,
      rs_percent: rsPercent !== "" ? Number(rsPercent) : null,
      description: description || null,
    };
    const response =
      await dealsPromotionsEndpointLogic.updateDealsPortfolioPlans(
        oauthTokenHeader,
        row.id,
        body,
      );
    if (response?.error) {
      setApiError(response.error);
      setIsSaving(false);
      return;
    }
    if (response) {
      const matchedCurrency = (currencies ?? []).find(
        (c: any) => c.shared_uid === body.currency_shared_uid,
      );
      const newStatus =
        body.cpa != null &&
        body.rs_percent != null &&
        body.currency_shared_uid != null
          ? "Complete"
          : "Partial";
      store.dispatch(
        updateDealsPortfolioPlan({
          id: row.id,
          changes: {
            cpa: body.cpa,
            rs_percent: body.rs_percent,
            rs: body.rs_percent != null ? `${body.rs_percent}%` : "",
            currency: matchedCurrency?.currency_code ?? null,
            currency_symbol: matchedCurrency?.currency_symbol ?? null,
            currency_shared_uid: body.currency_shared_uid,
            description: body.description,
            status: newStatus,
          },
        }),
      );
      store.dispatch(setLastUpdatedPlanId(row.id));
      setTimeout(() => store.dispatch(setLastUpdatedPlanId(null)), 2000);
      store.dispatch(
        setDealsSuccessMessage(
          `Deal plan for ${row.brand_name} (${row.brand_id}) updated successfully.`,
        ),
      );
      setIsSaving(false);
      closeDrawer();
      return;
    }
    setIsSaving(false);
  };

  const isSaveDisabled = isSaving;

  const EditDrawer = (
    <Box p={2} sx={{ width: 432 }} role="presentation">
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center">
          <Typography fontSize="20px" fontWeight={500} mr={1}>
            {row.brand_name ?? "N/A"}
          </Typography>
          {row.status === "Partial" ? (
            <Alert
              icon={
                <ErrorOutlineOutlinedIcon
                  fontSize="inherit"
                  sx={{ color: "#ED6C02", fontSize: "18px" }}
                />
              }
              severity="info"
              sx={{
                border: "1px solid #ED6C0280",
                padding: "0px 5px",
                display: "inline-flex",
                background: "#FFF4E5",
                color: "#ED6C02",
                fontSize: "13px",
                height: "25px",
                alignItems: "center",
                "& .MuiAlert-icon": { mr: 0.5 },
              }}
            >
              Partial
            </Alert>
          ) : (
            <Alert
              icon={
                <CheckCircleOutlinedIcon
                  fontSize="inherit"
                  sx={{ color: "rgba(30, 70, 32, 1)", fontSize: "18px" }}
                />
              }
              severity="success"
              sx={{
                border: "1px solid rgba(46, 125, 50, 0.5)",
                padding: "0px 5px",
                display: "inline-flex",
                fontSize: "13px",
                height: "25px",
                alignItems: "center",
                "& .MuiAlert-icon": { mr: 0.5 },
              }}
            >
              Complete
            </Alert>
          )}
        </Box>
        <IconButton
          onClick={closeDrawer}
          sx={{ width: "28px", height: "28px" }}
        >
          <ClearOutlinedIcon fontSize="medium" sx={{ color: "#0000008F" }} />
        </IconButton>
      </Box>

      <Box display="flex" alignItems="center" mt={0.5}>
        <Typography fontSize="12px" fontWeight={400}>
          Brand ID: {row.brand_id} &nbsp;·&nbsp;
        </Typography>
        <Chip
          label={row.website ?? "N/A"}
          sx={{
            backgroundColor: "#9c27b0",
            color: "#ffffff",
            height: "24px",
            fontWeight: "500",
          }}
        />
      </Box>

      <Typography fontSize="16px" fontWeight={500} my={3}>
        Plan Info
      </Typography>

      <Box>
        <Typography fontSize="12px" fontWeight={400}>
          Plan Term
        </Typography>
        <Typography fontSize="16px" mb={2}>
          {row.plan_term ?? "N/A"}
        </Typography>
      </Box>
      <Box>
        <Typography fontSize="12px" fontWeight={400}>
          Plan Name
        </Typography>
        <Typography fontSize="16px" mb={2}>
          {row.plan_name ?? "N/A"}
        </Typography>
      </Box>

      <Typography fontSize="16px" fontWeight={500} my={3}>
        Financial Details
      </Typography>

      <Box>
        <TextField
          color="secondary"
          fullWidth
          size="small"
          placeholder="Add CPA"
          label="CPA"
          type="number"
          value={cpa}
          onChange={(e) => { if (e.target.value.replace("-", "").length <= 11) setCpa(e.target.value); }}
          onBlur={() => setTouched((p) => ({ ...p, cpa: true }))}
          error={!!cpaError}
          helperText={cpaError ?? " "}
          sx={{ mb: 1 }}
          required
        />
        <TextField
          color="secondary"
          fullWidth
          size="small"
          placeholder="Add RS%"
          label="RS %"
          type="number"
          value={rsPercent}
          onChange={(e) => setRsPercent(e.target.value)}
          onBlur={() => setTouched((p) => ({ ...p, rsPercent: true }))}
          error={!!rsError}
          helperText={rsError ?? " "}
          sx={{ mb: 1 }}
          required
        />
        <Autocomplete
          disablePortal
          options={currencyOptions}
          value={selectedCurrency}
          loading={!currencies}
          onChange={(_, newValue) => {
            setSelectedCurrency(newValue);
            setTouched((p) => ({ ...p, currency: true }));
          }}
          onBlur={() => setTouched((p) => ({ ...p, currency: true }))}
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
          getOptionLabel={(option) =>
            option.symbol ? `${option.symbol} ${option.label}` : option.label
          }
          renderOption={(props, option) => (
            <li {...props} key={option.uid}>
              <span style={{ color: "rgba(0,0,0,0.56)", marginRight: 8 }}>
                {option.symbol}
              </span>
              {option.label}
            </li>
          )}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Currency *"
              color="secondary"
              error={!!currencyError}
              helperText={currencyError ?? " "}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!currencies && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Box>

      <Typography fontSize="16px" fontWeight={500} my={3}>
        Notes
      </Typography>

      <TextField
        fullWidth
        placeholder="Source of deal agreement..."
        color="secondary"
        size="small"
        label="Description (Optional)"
        multiline
        rows={4}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        error={!!descError}
        slotProps={{ htmlInput: { maxLength: 255 } }}
        sx={{
          "& .MuiFormHelperText-root": { paddingLeft: "16px" },
        }}
        helperText={
          descError ? descError : `Give context to this deal or add explanation`
        }
      />

      <Box justifyContent="flex-end" display="flex" mt={4}>
        <Button
          color="secondary"
          variant="outlined"
          onClick={closeDrawer}
          disabled={isSaving}
          data-tracker="796"
        >
          Cancel
        </Button>
        <Button
          sx={{ ml: "16px" }}
          disabled={isSaveDisabled}
          color="secondary"
          variant="contained"
          endIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          onClick={handleSave}
          data-tracker="795"
        >
          Save
        </Button>
      </Box>

      {apiError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setApiError(null)}
        >
          {apiError}
        </Alert>
      )}
    </Box>
  );

  return (
    <div>
      <IconButton
        onClick={openDrawer}
        disabled={disabled}
        sx={{ width: "40px", height: "40px", mx: 0.5 }}
        data-tracker="794"
      >
        <EditIcon
          color={disabled ? "disabled" : "secondary"}
          fontSize="medium"
        />
      </IconButton>

      <Drawer anchor="right" open={open} sx={{ zIndex: 9999 }}>
        {mounted && EditDrawer}
      </Drawer>
    </div>
  );
}
